import React from 'react';
import * as Sentry from '@sentry/react';
import { useErrorMonitoring } from '@/hooks/useErrorMonitoring';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{
    error: Error;
    resetError: () => void;
    errorInfo?: React.ErrorInfo;
  }>;
  component?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

class ErrorBoundaryClass extends React.Component<
  ErrorBoundaryProps & { reportError: (error: Error, component?: string, metadata?: any) => void },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { reportError: (error: Error, component?: string, metadata?: any) => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Report to Sentry with context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: true,
        component: this.props.component || 'Unknown',
      },
      level: 'error',
    });

    // Report to custom error monitoring (for email alerts)
    this.props.reportError(error, this.props.component, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      props: Object.keys(this.props).filter(key => key !== 'children' && key !== 'reportError')
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return (
          <FallbackComponent
            error={this.state.error}
            resetError={this.resetError}
            errorInfo={this.state.errorInfo}
          />
        );
      }

      // Default error UI
      return (
        <div className="min-h-[200px] flex items-center justify-center p-4">
          <Alert variant="destructive" className="max-w-md">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>שגיאה במערכת</AlertTitle>
            <AlertDescription className="mt-2 space-y-3">
              <p>אירעה שגיאה בטעינת הרכיב. הצוות שלנו קיבל התראה ויטפל בבעיה.</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={this.resetError}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-3 w-3" />
                  נסה שוב
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-3 w-3" />
                  טען מחדש
                </Button>
              </div>
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-3">
                  <summary className="cursor-pointer text-sm font-medium">
                    פרטים טכניים (פיתוח)
                  </summary>
                  <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                    {this.state.error.message}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}
            </AlertDescription>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  const { reportCriticalError } = useErrorMonitoring();

  const reportError = React.useCallback((error: Error, component?: string, metadata?: any) => {
    reportCriticalError(
      error,
      window.location.pathname,
      'component_error',
      component || props.component || 'ErrorBoundary',
      metadata
    );
  }, [reportCriticalError, props.component]);

  return <ErrorBoundaryClass {...props} reportError={reportError} />;
};

// HOC for wrapping components with error boundary
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  componentName?: string,
  fallback?: ErrorBoundaryProps['fallback']
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary component={componentName || Component.displayName || Component.name} fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${componentName || Component.displayName || Component.name})`;
  
  return WrappedComponent;
};